import { supabase } from "@/config/supabase";
import { User } from "@/types";
import { SUPABASE_TABLES } from "@/config/constants";
import { errorLogger, ErrorSeverity } from "./errorLogger.service";

const authDebugEnabled =
  !import.meta.env.PROD &&
  (import.meta.env.VITE_AUTH_DEBUG === "1" || import.meta.env.VITE_AUTH_DEBUG === "true");

const authDebug = (message: string, extra?: Record<string, unknown>) => {
  if (!authDebugEnabled) return;
  // Never log tokens. Only log high-level state.
  if (extra) {
    console.info(`[auth] ${message}`, extra); // eslint-disable-line no-console
  } else {
    console.info(`[auth] ${message}`); // eslint-disable-line no-console
  }
};

// ── Shared user cache ──────────────────────────────────────────────────
// Persist a minimal, non-sensitive snapshot of the last hydrated user so
// that onAuthStateChange and hydrateFromSession can recover from profile-
// fetch failures (e.g. brand-new account where the DB trigger is slow).
const USER_CACHE_KEY = "wihmedia-auth-user-cache";

const readCachedUser = (): User | null => {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Omit<User, "createdAt"> & {
      createdAt: string;
    };
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.uid || !parsed.email || !parsed.displayName || !parsed.role || !parsed.createdAt) {
      return null;
    }
    return {
      ...parsed,
      createdAt: new Date(parsed.createdAt),
    } as User;
  } catch {
    return null;
  }
};

const writeCachedUser = (user: User) => {
  try {
    localStorage.setItem(
      USER_CACHE_KEY,
      JSON.stringify({
        ...user,
        createdAt: user.createdAt.toISOString(),
      })
    );
  } catch {
    // ignore
  }
};

const clearCachedUser = () => {
  try {
    localStorage.removeItem(USER_CACHE_KEY);
  } catch {
    // ignore
  }
};

export interface RegisterResult {
  user: User;
  /** True when Supabase returned no session because email confirmation is required. */
  needsEmailConfirmation: boolean;
}

/** Thrown by loginUser when Supabase rejects login because the email hasn't been confirmed yet. */
export class EmailNotConfirmedError extends Error {
  readonly email: string;
  constructor(email: string) {
    super("Email not confirmed");
    this.name = "EmailNotConfirmedError";
    this.email = email;
  }
}

export const registerUser = async (
  email: string,
  password: string,
  displayName: string,
  role: "admin" | "customer" = "customer",
  captchaToken?: string
): Promise<RegisterResult> => {
  void role;

  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          displayName,
        },
        emailRedirectTo: `${window.location.origin}/auth/confirm`,
        ...(captchaToken ? { captchaToken } : {}),
      },
    });
    if (error) throw error;
    const supaUser = data.user!;

    // When email confirmation is enabled (production), signUp returns a user but
    // no session. The session is only issued after the user clicks the confirm link.
    const needsEmailConfirmation = !data.session;

    const user: User = {
      uid: supaUser.id,
      email: supaUser.email!,
      displayName,
      role: "customer",
      createdAt: new Date(),
    };

    // Set user context for error tracking
    errorLogger.setUser(user.uid, user.email, user.displayName);

    // Send welcome email asynchronously (only when we have a live session;
    // Supabase's own confirmation email handles the unconfirmed case).
    if (!needsEmailConfirmation && user.role === "customer") {
      const { emailService } = await import("./email.service");
      emailService.sendWelcomeEmail(displayName, email).catch((err) => {
        errorLogger.logError(err, ErrorSeverity.WARNING, {
          action: "sendWelcomeEmail",
          userId: user.uid,
        });
      });
    }

    return { user, needsEmailConfirmation };
  } catch (error) {
    errorLogger.logError(error as Error, ErrorSeverity.ERROR, {
      action: "registerUser",
      component: "auth.service",
    });
    throw error;
  }
};

export const loginUser = async (
  email: string,
  password: string,
  captchaToken?: string
): Promise<User> => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
      ...(captchaToken ? { options: { captchaToken } } : {}),
    });

    // Supabase returns a 400 "Email not confirmed" error when the user has not
    // yet clicked the verification link.  Crucially, Supabase *still* populates
    // data.user in the response body even with this error, so we must check for
    // the error message *before* inspecting data.user — otherwise the error is
    // silently swallowed and login appears to succeed without a real session.
    const isEmailNotConfirmed =
      error?.message?.toLowerCase().includes("email not confirmed") ||
      error?.message?.toLowerCase().includes("email_not_confirmed");

    if (isEmailNotConfirmed) throw new EmailNotConfirmedError(email);
    if (error) throw error;
    if (!data.user) throw new Error("Login failed");

    const supaUser = data.user;

    // Build user from auth metadata first (fast path)
    // The onAuthStateChange listener will hydrate the full profile async
    const user: User = {
      uid: supaUser.id,
      email: supaUser.email!,
      displayName: supaUser.user_metadata?.displayName || supaUser.email!.split("@")[0],
      role: "customer",
      createdAt: new Date(),
      email_confirmed_at: supaUser.email_confirmed_at,
    };

    // Try to get profile, but don't block login on it
    try {
      const { data: profile } = await supabase
        .from(SUPABASE_TABLES.USERS)
        .select("id,email,displayName,role,phoneNumber,created_at")
        .eq("id", supaUser.id)
        .single();

      if (profile) {
        user.displayName = profile.displayName || user.displayName;
        user.role = (profile.role as "admin" | "customer") || user.role;
        user.phoneNumber = profile.phoneNumber;
        user.createdAt = profile.created_at ? new Date(profile.created_at) : user.createdAt;
        // email_confirmed_at comes from auth, not profile table
      }
    } catch {
      // Profile fetch failed - continue with auth-only user data
    }

    // Set user context for error tracking
    errorLogger.setUser(user.uid, user.email, user.displayName);

    // Write user to shared cache so the async onAuthStateChange SIGNED_IN
    // handler finds a valid lastStableUser (with the correct role) instead
    // of building a degraded fallback or emitting null.
    writeCachedUser(user);

    return user;
  } catch (error) {
    errorLogger.logError(error as Error, ErrorSeverity.ERROR, {
      action: "loginUser",
      component: "auth.service",
    });
    throw error;
  }
};

export const logoutUser = async (): Promise<void> => {
  try {
    // Clear user context from error tracking
    errorLogger.clearUser();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  } catch (error) {
    errorLogger.logError(error as Error, ErrorSeverity.ERROR, {
      action: "logoutUser",
      component: "auth.service",
    });
    throw error;
  }
};

export const resendVerificationEmail = async (email: string): Promise<void> => {
  try {
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/confirm`,
      },
    });
    if (error) throw error;
  } catch (error) {
    errorLogger.logError(error as Error, ErrorSeverity.ERROR, {
      action: "resendVerificationEmail",
      component: "auth.service",
    });
    throw error;
  }
};

export const sendPasswordReset = async (email: string): Promise<void> => {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });
    if (error) throw error;
  } catch (error) {
    errorLogger.logError(error as Error, ErrorSeverity.ERROR, {
      action: "sendPasswordReset",
      component: "auth.service",
    });
    throw error;
  }
};

export const updatePassword = async (newPassword: string): Promise<void> => {
  try {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  } catch (error) {
    errorLogger.logError(error as Error, ErrorSeverity.ERROR, {
      action: "updatePassword",
      component: "auth.service",
    });
    throw error;
  }
};

export const getCurrentUser = async (uid: string, retryCount = 0): Promise<User | null> => {
  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 800;
  const start = typeof performance !== "undefined" ? performance.now() : Date.now();

  authDebug("getCurrentUser: start", { uid, retryCount });

  try {
    const { data, error } = await supabase
      .from(SUPABASE_TABLES.USERS)
      .select("id,email,displayName,role,phoneNumber,created_at")
      .eq("id", uid)
      .single();
    if (error || !data) {
      authDebug("getCurrentUser: missing", {
        uid,
        retryCount,
        hasError: Boolean(error),
        errorMessage: (error as any)?.message,
      });
      // Retry on network/timeout errors OR when the profile row isn't found yet.
      // New accounts: the handle_new_user trigger may have a small propagation
      // delay, so PGRST116 ("not found") should also be retried.
      const isNotFound = error?.code === "PGRST116" || (!data && !error);
      const isNetworkError =
        error?.message?.includes("fetch") || error?.message?.includes("timeout");
      if (retryCount < MAX_RETRIES && (isNetworkError || isNotFound)) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * (retryCount + 1)));
        return getCurrentUser(uid, retryCount + 1);
      }
      return null;
    }

    authDebug("getCurrentUser: success", {
      uid,
      retryCount,
      ms: Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - start),
    });

    return {
      uid: data.id,
      email: data.email,
      displayName: data.displayName,
      role: data.role as "admin" | "customer",
      phoneNumber: data.phoneNumber,
      createdAt: data.created_at ? new Date(data.created_at) : new Date(),
    };
  } catch (error) {
    authDebug("getCurrentUser: error", {
      uid,
      retryCount,
      message: (error as Error)?.message,
      ms: Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - start),
    });
    // Retry on network errors
    if (
      retryCount < MAX_RETRIES &&
      error instanceof Error &&
      (error.message.includes("fetch") ||
        error.message.includes("timeout") ||
        error.message.includes("network"))
    ) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * (retryCount + 1)));
      return getCurrentUser(uid, retryCount + 1);
    }

    errorLogger.logError(error as Error, ErrorSeverity.ERROR, {
      action: "getCurrentUser",
      component: "auth.service",
      userId: uid,
      metadata: { retryCount },
    });
    return null;
  }
};

export const onAuthStateChange = (callback: (user: User | null) => void): (() => void) => {
  // Keep the last successfully hydrated user so transient auth/network hiccups
  // (especially during TOKEN_REFRESHED / emulator restarts / slow DB) don't
  // force the UI into a logged-out redirect.
  let lastStableUser: User | null = null;

  // Use module-scope readCachedUser / writeCachedUser / clearCachedUser.
  lastStableUser = readCachedUser();

  // Avoid spamming profile fetches on chatty auth events (e.g. TOKEN_REFRESHED).
  // Keep role checks correct by always fetching on SIGNED_IN/USER_UPDATED.
  let inFlightProfileHydration: Promise<void> | null = null;
  let lastProfileHydratedAtMs = 0;
  const MIN_PROFILE_REFRESH_INTERVAL_MS = 2 * 60 * 1000;

  class AuthHydrationTimeoutError extends Error {
    public readonly timeoutMs: number;

    constructor(timeoutMs: number) {
      super(`Auth session hydration timeout after ${timeoutMs}ms`);
      this.name = "AuthHydrationTimeoutError";
      this.timeoutMs = timeoutMs;
    }
  }

  // Local Supabase emulator + container restarts can make the initial auth session
  // hydration slower than expected (especially during E2E/visual test runs).
  // A longer timeout avoids a late timeout callback clearing a valid session.
  const AUTH_HYDRATION_TIMEOUT_MS = 60000;
  const PROFILE_FETCH_TIMEOUT_MS = 15000;

  const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new AuthHydrationTimeoutError(timeoutMs)), timeoutMs)
      ),
    ]);
  };

  const hydrateFromSession = async () => {
    try {
      authDebug("hydrateFromSession: start");
      const { data, error } = await withTimeout(
        supabase.auth.getSession(),
        AUTH_HYDRATION_TIMEOUT_MS
      );
      if (error) throw error;
      const supaUser = data.session?.user;

      authDebug("hydrateFromSession: got session", {
        hasSession: Boolean(data.session),
        hasUser: Boolean(supaUser),
      });

      if (!supaUser) {
        errorLogger.clearUser();
        lastStableUser = null;
        clearCachedUser();
        callback(null);
        return;
      }

      // If we have a cached user for this session, emit it immediately so the UI
      // never gets stuck behind the auth gate while we re-hydrate the profile.
      if (lastStableUser && lastStableUser.uid === supaUser.id) {
        callback(lastStableUser);
      }

      const user = await withTimeout(getCurrentUser(supaUser.id), PROFILE_FETCH_TIMEOUT_MS);
      if (!user) {
        authDebug("hydrateFromSession: profile missing/transient", {
          event: "hydrateFromSession",
          hasLastStableUser: Boolean(lastStableUser),
        });

        if (lastStableUser) {
          // Keep the cached user — don't clear a valid session.
          // Still update email_confirmed_at from the auth session in case
          // the user just verified and the cached version is stale.
          if (supaUser.email_confirmed_at) {
            lastStableUser = { ...lastStableUser, email_confirmed_at: supaUser.email_confirmed_at };
            writeCachedUser(lastStableUser);
          }
          callback(lastStableUser);
          return;
        }

        // No cached user and no profile row. This happens on first login
        // when the DB trigger hasn't created the profile yet, or if the
        // page is reloaded immediately after signup.  Build a minimal
        // fallback from Supabase auth metadata so the user isn't kicked
        // back to /login.
        const fallback: User = {
          uid: supaUser.id,
          email: supaUser.email!,
          displayName: supaUser.user_metadata?.displayName || supaUser.email!.split("@")[0],
          role: "customer",
          createdAt: new Date(),
          email_confirmed_at: supaUser.email_confirmed_at,
        };
        errorLogger.setUser(fallback.uid, fallback.email, fallback.displayName);
        lastStableUser = fallback;
        writeCachedUser(fallback);
        authDebug("hydrateFromSession: using fallback auth-metadata user", {
          uid: fallback.uid,
        });
        callback(fallback);
        return;
      }

      // email_confirmed_at lives on the Supabase auth user, not the profile
      // table. Merge it so the UI knows the email is verified.
      user.email_confirmed_at = supaUser.email_confirmed_at ?? user.email_confirmed_at;

      errorLogger.setUser(user.uid, user.email, user.displayName);
      lastStableUser = user;
      writeCachedUser(user);
      authDebug("hydrateFromSession: hydrated user", {
        uid: user.uid,
        role: user.role,
      });
      callback(user);
    } catch (error) {
      if (error instanceof AuthHydrationTimeoutError) {
        authDebug("hydrateFromSession: timeout", {
          timeoutMs: error.timeoutMs,
        });
        // Only log timeout warnings in development mode
        if (!import.meta.env.PROD) {
          errorLogger.logError(error, ErrorSeverity.WARNING, {
            action: "hydrateFromSession",
            component: "auth.service",
            metadata: { timeoutMs: error.timeoutMs },
          });
        }

        // Avoid hanging forever behind the auth gate.
        // If we have a cached user, keep the UI usable.
        // Otherwise, resolve to null so ProtectedRoute can redirect.
        if (lastStableUser) {
          callback(lastStableUser);
        } else {
          errorLogger.clearUser();
          callback(null);
        }
        return;
      }

      errorLogger.logError(error as Error, ErrorSeverity.ERROR, {
        action: "hydrateFromSession",
        component: "auth.service",
      });

      authDebug("hydrateFromSession: error", {
        message: (error as Error)?.message,
        hasLastStableUser: Boolean(lastStableUser),
      });
      if (lastStableUser) {
        callback(lastStableUser);
      } else {
        errorLogger.clearUser();
        callback(null);
      }
    }
  };

  void hydrateFromSession();

  const hydrateProfileForSessionUser = async (event: any, supaUser: any) => {
    // Deduplicate concurrent profile hydrations.
    if (inFlightProfileHydration) return inFlightProfileHydration;

    inFlightProfileHydration = (async () => {
      try {
        const user = await withTimeout(getCurrentUser(supaUser.id), PROFILE_FETCH_TIMEOUT_MS);

        if (!user) {
          authDebug("onAuthStateChange: profile missing/transient", {
            event,
            hasLastStableUser: Boolean(lastStableUser),
          });

          // On SIGNED_IN/USER_UPDATED with no profile row and no cached user,
          // this is likely a brand-new account where the handle_new_user trigger
          // hasn't propagated yet. Retry a few times before giving up.
          if ((event === "SIGNED_IN" || event === "USER_UPDATED") && !lastStableUser) {
            const NEW_ACCOUNT_RETRIES = 4;
            const NEW_ACCOUNT_RETRY_DELAY_MS = 600;
            let retried: User | null = null;
            for (let i = 0; i < NEW_ACCOUNT_RETRIES; i++) {
              await new Promise((resolve) =>
                setTimeout(resolve, NEW_ACCOUNT_RETRY_DELAY_MS * (i + 1))
              );
              retried = await getCurrentUser(supaUser.id);
              if (retried) break;
            }
            if (retried) {
              errorLogger.setUser(retried.uid, retried.email, retried.displayName);
              lastStableUser = retried;
              writeCachedUser(retried);
              lastProfileHydratedAtMs = Date.now();
              callback(retried);
              return;
            }
            // Profile row doesn't exist yet after retries (DB trigger may
            // be slow on first login). Prefer the cached user written by
            // loginUser() — it has the correct role from the profile fetch
            // that loginUser already performed.  Fall back to auth metadata
            // only if no cache exists.
            const cached = readCachedUser();
            const fallbackUser: User =
              cached && cached.uid === supaUser.id
                ? cached
                : {
                    uid: supaUser.id,
                    email: supaUser.email!,
                    displayName:
                      supaUser.user_metadata?.displayName || supaUser.email!.split("@")[0],
                    role: "customer",
                    createdAt: new Date(),
                    email_confirmed_at: supaUser.email_confirmed_at,
                  };
            errorLogger.setUser(fallbackUser.uid, fallbackUser.email, fallbackUser.displayName);
            lastStableUser = fallbackUser;
            writeCachedUser(fallbackUser);
            lastProfileHydratedAtMs = Date.now();
            authDebug("onAuthStateChange: using fallback user (profile not yet ready)", {
              uid: fallbackUser.uid,
              role: fallbackUser.role,
              fromCache: Boolean(cached && cached.uid === supaUser.id),
              event,
            });
            callback(fallbackUser);
            return;
          }

          // Keep the last stable user if we have one.
          if (lastStableUser) {
            callback(lastStableUser);
            return;
          }

          // No stable user: resolve to null (avoid auth gate hanging).
          errorLogger.clearUser();
          callback(null);
          return;
        }

        // email_confirmed_at lives on the Supabase auth user, not the profile
        // table. Merge it so the UI knows the email is verified.
        user.email_confirmed_at = supaUser.email_confirmed_at ?? user.email_confirmed_at;

        errorLogger.setUser(user.uid, user.email, user.displayName);
        lastStableUser = user;
        writeCachedUser(user);
        lastProfileHydratedAtMs = Date.now();
        authDebug("onAuthStateChange: hydrated user", {
          uid: user.uid,
          role: user.role,
          event,
        });
        callback(user);
      } catch (error) {
        errorLogger.logError(error as Error, ErrorSeverity.ERROR, {
          action: "onAuthStateChange",
          component: "auth.service",
          metadata: { event },
        });

        authDebug("onAuthStateChange: error", {
          event,
          message: (error as Error)?.message,
          hasLastStableUser: Boolean(lastStableUser),
        });

        if (lastStableUser) {
          callback(lastStableUser);
          return;
        }

        errorLogger.clearUser();
        callback(null);
      } finally {
        inFlightProfileHydration = null;
      }
    })();

    return inFlightProfileHydration;
  };

  const handleAuthEvent = async (event: any, session: any) => {
    const supaUser = session?.user;
    authDebug("onAuthStateChange", {
      event,
      hasSession: Boolean(session),
      hasUser: Boolean(supaUser),
      hasLastStableUser: Boolean(lastStableUser),
    });

    // Handle explicit sign-out or session expiration
    if (event === "SIGNED_OUT" || !supaUser) {
      authDebug("onAuthStateChange: clearing session", { event });
      errorLogger.clearUser();
      lastStableUser = null;
      clearCachedUser();
      callback(null);
      return;
    }

    // Re-read the cache in case loginUser() wrote a fresh user between
    // the last time we checked and this event firing.
    if (!lastStableUser || lastStableUser.uid !== supaUser.id) {
      const cached = readCachedUser();
      if (cached && cached.uid === supaUser.id) {
        lastStableUser = cached;
      }
    }

    // If we already have a cached user for this uid, emit it immediately.
    // This keeps the UI responsive while we re-hydrate the full profile.
    // Always refresh email_confirmed_at from the live auth session.
    if (lastStableUser && lastStableUser.uid === supaUser.id) {
      if (
        supaUser.email_confirmed_at &&
        lastStableUser.email_confirmed_at !== supaUser.email_confirmed_at
      ) {
        lastStableUser = { ...lastStableUser, email_confirmed_at: supaUser.email_confirmed_at };
        writeCachedUser(lastStableUser);
      }
      callback(lastStableUser);
    }

    // Best practice per Supabase docs: onAuthStateChange can fire frequently.
    // Avoid refetching DB profile on TOKEN_REFRESHED.
    if (event === "TOKEN_REFRESHED") {
      return;
    }

    // Always refresh on SIGNED_IN / USER_UPDATED to keep role checks correct.
    if (event === "SIGNED_IN" || event === "USER_UPDATED") {
      await hydrateProfileForSessionUser(event, supaUser);
      return;
    }

    // For INITIAL_SESSION (and other events), only refresh occasionally.
    const now = Date.now();
    const shouldRefresh =
      !lastStableUser ||
      lastStableUser.uid !== supaUser.id ||
      now - lastProfileHydratedAtMs > MIN_PROFILE_REFRESH_INTERVAL_MS;

    if (shouldRefresh) {
      await hydrateProfileForSessionUser(event, supaUser);
    }
  };

  const { data: listener } = supabase.auth.onAuthStateChange((event: any, session: any) => {
    // Supabase recommends keeping this callback quick and not awaiting other Supabase calls
    // to avoid deadlocks. Dispatch async work right after this callback returns.
    setTimeout(() => {
      void handleAuthEvent(event, session);
    }, 0);
  });
  return () => {
    listener?.subscription.unsubscribe();
  };
};
